import { Box, Grid } from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextArea,
  CustomTextField,
  renderTwoLineOption,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import {
  SOURCE_TYPE_LABELS,
  SUPPLY_ORDER_FORM_TEXTS as TEXTS,
} from '../constants/supplyOrdersConstants';
import SupplyOrderMaterialsSection from './SupplyOrderMaterialsSection';
import SupplyOrderDestinationFields from './SupplyOrderDestinationFields';
import { toProviderOptions } from '../../utils/toProviderOptions';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';

const SOURCE_TYPE_OPTIONS = Object.entries(SOURCE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/**
 * Datos de cabecera y materiales de una orden a proveedor o a depósito.
 * Se usa en el alta y en la edición del borrador.
 * @param {Object} props
 * @returns {JSX.Element}
 */
const SupplyOrderFormSection = ({
  selectedOrder,
  onInputChange,
  onSelectChange,
  warehouses = [],
  warehousesLoading = false,
  providers = [],
  providersLoading = false,
  projects = [],
  projectsLoading = false,
  projectsError,
  onRetryProjects,
  destinationErrors = {},
  isSaving = false,
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
  isMobile = false,
  providerOnly = false,
}) => {
  const isProviderSource = providerOnly || selectedOrder.source_type === 'provider';
  const isWarehouseSource = !providerOnly && selectedOrder.source_type === 'warehouse';
  const warehouseOptions = toWarehouseOptions(warehouses);
  const providerOptions = toProviderOptions(providers);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isProviderSource && (
        <SupplyOrderDestinationFields
          selectedOrder={selectedOrder}
          onChange={onInputChange}
          projects={projects}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
          onRetryProjects={onRetryProjects}
          errors={destinationErrors}
          disabled={isSaving}
        />
      )}
      <Grid container spacing={2}>
        {!providerOnly && (
          <Grid item xs={12} md={4}>
            <CustomAutoComplete
              label={TEXTS.sourceType}
              name="source_type"
              value={selectedOrder.source_type}
              options={SOURCE_TYPE_OPTIONS}
              onChange={onSelectChange}
            />
          </Grid>
        )}

        {isProviderSource && (
          <Grid item xs={12} md={providerOnly ? 6 : 4}>
            <CustomAutoComplete
              label={providerOnly ? TEXTS.providerOptional : TEXTS.provider}
              name="uuid_provider_legal_entity"
              value={selectedOrder.uuid_provider_legal_entity}
              options={providerOptions}
              renderOption={renderTwoLineOption}
              inputPlaceholder={TEXTS.searchProvider}
              loading={providersLoading}
              ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
              onChange={onSelectChange}
            />
          </Grid>
        )}

        {isWarehouseSource && (
          <Grid item xs={12} md={4}>
            <CustomAutoComplete
              label={TEXTS.sourceWarehouse}
              name="uuid_source_warehouse"
              value={selectedOrder.uuid_source_warehouse}
              options={warehouseOptions}
              renderOption={renderTwoLineOption}
              inputPlaceholder={TEXTS.searchWarehouse}
              loading={warehousesLoading}
              ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
              onChange={onSelectChange}
            />
          </Grid>
        )}

        <Grid item xs={12} md={providerOnly ? 6 : 4}>
          <CustomAutoComplete
            label={TEXTS.destinationWarehouse}
            name="uuid_destination_warehouse"
            value={selectedOrder.uuid_destination_warehouse}
            options={warehouseOptions}
            renderOption={renderTwoLineOption}
            inputPlaceholder={TEXTS.searchWarehouse}
            loading={warehousesLoading}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            onChange={onSelectChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomTextField
            label={TEXTS.requiredAt}
            name="required_at"
            type="date"
            value={selectedOrder.required_at}
            onChange={onInputChange}
            shrinkLabel
          />
        </Grid>

        {isProviderSource && !providerOnly && (
          <Grid item xs={12} md={4}>
            <CustomTextField
              label={TEXTS.commitment}
              name="provider_delivery_commitment_at"
              type="date"
              value={selectedOrder.provider_delivery_commitment_at}
              onChange={onInputChange}
              shrinkLabel
            />
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <CustomTextField
            label={TEXTS.deliveryPlace}
            name="delivery_place"
            value={selectedOrder.delivery_place}
            onChange={onInputChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomTextField
            label={TEXTS.deliveryContact}
            name="delivery_contact_name"
            value={selectedOrder.delivery_contact_name}
            onChange={onInputChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomTextField
            label={TEXTS.deliveryPhone}
            name="delivery_contact_phone"
            value={selectedOrder.delivery_contact_phone}
            onChange={onInputChange}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <CustomTextField
            label={TEXTS.conditions}
            name="conditions"
            value={selectedOrder.conditions}
            onChange={onInputChange}
          />
        </Grid>

        <Grid item xs={12}>
          <CustomTextArea
            label={TEXTS.observations}
            name="observations"
            value={selectedOrder.observations}
            onChange={onInputChange}
            minRows={isMobile ? 2 : 3}
          />
        </Grid>
      </Grid>

      <SupplyOrderMaterialsSection
        materials={selectedOrder.materials}
        searchEnabled={searchEnabled}
        onAddMaterial={onAddMaterial}
        onUpdateMaterialQuantity={onUpdateMaterialQuantity}
        onRemoveMaterial={onRemoveMaterial}
      />
    </Box>
  );
};

export default SupplyOrderFormSection;
